import { ROUTES } from 'app/utils/constants/paths';
import { isEdelvives } from 'app/utils/utils';
import { saveReturnLocation } from 'app/utils/sessionReturnLocation';
import useInterval from 'core/hooks/useInterval';
import * as coreEntities from 'core/store/index';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

/**
 * Default warning window: show the dialog 5 minutes before the token expires.
 * Exported so tests can inject a short window.
 */
export const DEFAULT_WARNING_WINDOW_MS = 5 * 60 * 1000;

const TICK_MS = 1000;
const CRITICAL_THRESHOLD_SECONDS = 60;

/**
 * Milestone keys used to announce state changes only at threshold crossings.
 * The `${number}m` variants carry the whole minutes remaining above the
 * critical band, so the announcement reflects the real time left.
 */
type MilestoneKey = '' | `${number}m` | '60s' | '30s' | '10s' | 'expired';

type UseSessionTimeoutParams = {
  warningWindowMs?: number;
};

type UseSessionTimeout = {
  accessibleLabel: string;
  announcement: string;
  formattedTime: string;
  isCritical: boolean;
  isOpen: boolean;
  onContinue: () => void;
  onLogout: () => void;
  remainingSeconds: number;
  t: (key: string) => string;
};

function getCurrentPath(): string {
  return `${globalThis.location.pathname}${globalThis.location.search}`;
}

function navigateTo(path: string): void {
  globalThis.location.href = path;
}

// The hook is mounted globally (outside the router), so it also runs on
// `/logout` and `/auth/login`, where it must stay inert. `includes` (not
// `===`) keeps it matching under a router `basename` prefix.
function isAuthOrLogoutRoute(): boolean {
  const { pathname } = globalThis.location;
  return pathname.includes(ROUTES.LOGOUT) || pathname.includes(ROUTES.AUTH_LOGIN);
}

function formatTime(totalSeconds: number): string {
  const safeSeconds = Math.max(0, totalSeconds);
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Derives the milestone key from the remaining seconds. Only the crossings we
 * care about produce a distinct key, so the accessible announcement changes a
 * handful of times (not every second).
 */
function getMilestoneKey(isOpen: boolean, remainingSeconds: number): MilestoneKey {
  if (!isOpen) return '';
  if (remainingSeconds <= 0) return 'expired';
  if (remainingSeconds <= 10) return '10s';
  if (remainingSeconds <= 30) return '30s';
  if (remainingSeconds <= CRITICAL_THRESHOLD_SECONDS) return '60s';
  // Above the critical band, announce the whole minutes actually remaining
  // (rounded up) so a dialog mounted mid-window — e.g. a backgrounded tab that
  // revives with less than the full window left — never overstates the time.
  // Only minute boundaries produce a new key, so the announcement still changes
  // just a handful of times (not every second).
  const minutes = Math.ceil(remainingSeconds / 60);
  return `${minutes}m`;
}

/**
 * Session timeout logic (WCAG 2.2.1). Watches the persisted `tokenExpiration`
 * and, `warningWindowMs` before it, opens a warning dialog with a live
 * countdown. "Continue" re-authenticates the user (there is no silent token
 * renewal); "Log out" and expiry both route through `/logout` for a consistent,
 * tenant-agnostic teardown. Only the explicit "Continue" choice stashes the
 * pre-logout location so the user is returned to it after logging back in —
 * expiry and normal logout intentionally fall back to the default home page.
 */
export default function useSessionTimeout({ warningWindowMs = DEFAULT_WARNING_WINDOW_MS }: UseSessionTimeoutParams = {}): UseSessionTimeout {
  const { t } = useTranslation('auth');
  const dispatch = useDispatch();

  // Read once at render: navigation is a hard reload, so the route cannot
  // change under a mounted instance.
  const isAuthRoute = isAuthOrLogoutRoute();

  const authToken = useSelector((state) => coreEntities.auth.selectors.getAuthToken(state));
  const tokenExpiration = useSelector((state) => coreEntities.auth.selectors.getTokenExpiration(state));

  const [isOpen, setIsOpen] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [announcement, setAnnouncement] = useState('');

  // One-shot guard so the expiry teardown fires exactly once.
  const hasExpiredRef = useRef(false);
  // Avoid state updates after unmount (e.g. a tick landing during teardown).
  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const hasSession = Boolean(authToken) && Boolean(tokenExpiration);
  const warnAt = tokenExpiration ? tokenExpiration - warningWindowMs : null;

  const handleExpiry = useCallback(() => {
    // On an auth/logout route the logout page owns the teardown; navigating
    // from here would loop.
    if (isAuthRoute) return;
    if (hasExpiredRef.current) return;
    hasExpiredRef.current = true;
    setIsExpired(true);
    setIsOpen(false);
    // Clear the session before leaving (mirrors onContinue).
    dispatch(coreEntities.auth.actions.logout());
    // No return location on expiry: only an explicit "stay signed in" choice
    // (see onContinue) brings the user back to where they were. A timed-out or
    // otherwise abandoned session lands on the default post-login page (home).
    navigateTo(ROUTES.LOGOUT);
  }, [dispatch, isAuthRoute]);

  // Immediate expiry on mount: never show a stuck 0s dialog for an already
  // expired token — tear the session down right away. handleExpiry is inert on
  // auth/logout routes.
  useEffect(() => {
    if (hasSession && tokenExpiration && Date.now() >= tokenExpiration) {
      handleExpiry();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the dialog inert when there is no session (e.g. after logout).
  useEffect(() => {
    if (!hasSession) {
      setIsOpen(false);
    }
  }, [hasSession]);

  // Recompute remaining time from the wall clock each tick to avoid drift and
  // background-tab throttling. Paused (delay=null) while there is no session,
  // or on auth/logout routes where the tick must never navigate.
  useInterval(
    () => {
      if (!isMountedRef.current || !hasSession || !tokenExpiration || warnAt === null) return;

      const now = Date.now();
      const remainingMs = tokenExpiration - now;

      if (remainingMs <= 0) {
        setRemainingSeconds(0);
        handleExpiry();
        return;
      }

      if (now >= warnAt) {
        setIsOpen(true);
        setRemainingSeconds(Math.max(0, Math.ceil(remainingMs / 1000)));
      }
    },
    hasSession && !isExpired && !isAuthRoute ? TICK_MS : null
  );

  // Announce only at milestone crossings. Start empty and populate after the
  // crossing so screen readers reliably read the change (mirrors QuizzTimer).
  const milestoneKey = getMilestoneKey(isOpen, remainingSeconds);
  useEffect(() => {
    const minutesMatch = /^(\d+)m$/.exec(milestoneKey);
    if (minutesMatch) {
      setAnnouncement(
        t('auth:Your session will expire in {{minutes}} minutes', { minutes: Number(minutesMatch[1]) })
      );
      return;
    }
    const messages: Record<Exclude<MilestoneKey, `${number}m`>, string> = {
      '': '',
      '60s': t('auth:Your session will expire in 1 minute'),
      '30s': t('auth:Your session will expire in 30 seconds'),
      '10s': t('auth:Your session will expire in 10 seconds'),
      expired: t('auth:Your session has expired'),
    };
    setAnnouncement(messages[milestoneKey as Exclude<MilestoneKey, `${number}m`>] ?? '');
  }, [milestoneKey, t]);

  const onContinue = useCallback(() => {
    // Stash the current page so login can return the user here (see login-return
    // wiring in redirectFromLoginWithToken.js and Routes.js).
    saveReturnLocation(getCurrentPath());
    // Clear the current (still valid) session so the login screen is reachable.
    dispatch(coreEntities.auth.actions.logout());
    // Edelvives re-auth goes through MSAL logoutRedirect, which bounces the user
    // back to sign in; the standard tenant lands directly on the login page.
    navigateTo(isEdelvives() ? ROUTES.LOGOUT : ROUTES.AUTH_LOGIN);
  }, [dispatch]);

  const onLogout = useCallback(() => {
    navigateTo(ROUTES.LOGOUT);
  }, []);

  const isCritical = isOpen && remainingSeconds <= CRITICAL_THRESHOLD_SECONDS;
  const formattedTime = formatTime(remainingSeconds);

  // State conveyed by text, not color alone: the label carries the urgency.
  const statusSuffix = isCritical ? `. ${t('auth:Session expiring soon')}` : '';
  const accessibleLabel = `${t('auth:Time remaining')}: ${formattedTime}${statusSuffix}`;

  return {
    accessibleLabel,
    announcement,
    formattedTime,
    isCritical,
    isOpen,
    onContinue,
    onLogout,
    remainingSeconds,
    t,
  };
}
